import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  hint: { fontSize: 13, color: theme.app.textSecondary, marginBottom: 8, lineHeight: 1.5 },
  label: { fontSize: 12, fontWeight: 600, color: theme.app.ink, margin: "12px 0 6px", textTransform: "uppercase", letterSpacing: ".04em" },
  area: { width: "100%", minHeight: 120, fontFamily: theme.app.mono, fontSize: 12.5, padding: 10, border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, resize: "vertical", boxSizing: "border-box" },
  err: { color: theme.app.danger, fontSize: 13, marginTop: 8 },
  resHead: { display: "flex", alignItems: "center", gap: 8, marginTop: 4 },
  badge: { fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 6, color: "#fff" },
  pre: { maxHeight: 260, overflow: "auto", background: theme.app.hover, border: `1px solid ${theme.app.border}`, borderRadius: 8, padding: 10, fontFamily: theme.app.mono, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: "6px 0 0" },
}));

// coleta os caminhos {{...}} usados no nó
const collectRefs = node => {
  const set = new Set();
  const scan = v => {
    if (typeof v === "string") {
      const re = /\{\{\s*([^}]+?)\s*\}\}/g;
      let m;
      while ((m = re.exec(v))) set.add(m[1].trim());
    } else if (v && typeof v === "object") Object.values(v).forEach(scan);
  };
  scan(node.url); scan(node.headers); scan(node.body);
  return [...set];
};

// monta um objeto aninhado {a:{b:""}} a partir dos caminhos "a.b"
const skeletonFrom = paths => {
  const root = {};
  paths.forEach(p => {
    const parts = p.split(".");
    let cur = root;
    parts.forEach((k, i) => {
      if (i === parts.length - 1) cur[k] = cur[k] || "";
      else cur = cur[k] = cur[k] || {};
    });
  });
  return root;
};

const HttpTestModal = ({ open, onClose, node, onResult }) => {
  const c = useStyles();
  const initial = useMemo(() => JSON.stringify(skeletonFrom(collectRefs(node || {})), null, 2), [node]);
  const [scopeText, setScopeText] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  React.useEffect(() => { setScopeText(initial); setResult(null); setError(""); }, [initial, open]);

  const run = async () => {
    setError(""); setResult(null);
    let scope;
    try { scope = scopeText.trim() ? JSON.parse(scopeText) : {}; }
    catch { setError("Os valores de exemplo não são um JSON válido."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/applications/test-node", { node, scope });
      setResult(data);
      // disponibiliza a saída no picker de variáveis (como uma execução real)
      if (onResult && data && data.output) onResult(data.output);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || "Falha ao enviar o teste.");
    } finally {
      setLoading(false);
    }
  };

  const out = result && result.output;
  const okColor = out && out.ok ? "#1E9E62" : "#C0453B";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar teste — {node && node.method} {node && node.url ? "" : ""}</DialogTitle>
      <DialogContent>
        <div className={c.hint}>
          Dispara a requisição HTTP de verdade, com estes valores de exemplo no lugar das variáveis
          <code> {"{{...}}"}</code>. Edite conforme necessário.
        </div>
        <div className={c.label}>Valores de exemplo</div>
        <textarea className={c.area} value={scopeText} onChange={e => setScopeText(e.target.value)} spellCheck={false} />
        {error && <div className={c.err}>{error}</div>}
        {out && (
          <>
            <div className={c.label}>Resposta</div>
            <div className={c.resHead}>
              <span className={c.badge} style={{ background: okColor }}>{out.status || "erro"}</span>
              <span style={{ fontSize: 12.5, color: "#5B6470" }}>{out.ok ? "sucesso (2xx)" : "falhou"}</span>
            </div>
            <pre className={c.pre}>{out.json != null ? JSON.stringify(out.json, null, 2) : String(out.body || "")}</pre>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" color="primary" onClick={run} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
          {loading ? "Enviando…" : "Enviar teste"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HttpTestModal;
